/**
 * Alle Nederlandse teksten van het dashboard. Jij-vorm, gewone taal: elke kaart
 * zegt wat er is en wat je ermee doet. Labels zijn zelfstandige naamwoorden,
 * knoppen zijn werkwoorden.
 */
export const dashboardCopy = {
    /* Zoals in de rail. */
    title: 'Dashboard',
    eyebrowFallback: 'Deze week',
    eyebrow: (week: number, inspections: number, openReports: number) =>
        `week ${week} · ${inspections} ${inspections === 1 ? 'keuring' : 'keuringen'} · ${openReports} open ${
            openReports === 1 ? 'rapport' : 'rapporten'
        }`,
    /** Een bedrijf zonder keuringen: nog geen week om samen te vatten. */
    eyebrowWelcome: 'Welkom bij Smartkeur',
    newInspection: 'Nieuwe keuring',

    kpiSectionLabel: 'Kerncijfers',
    kpiVolume: 'Keuringen deze week',
    kpiTurnaround: 'Tijd tot rapport',
    kpiOpenReports: 'Open rapporten',
    kpiToInvoice: 'Nog te factureren',
    unknown: 'onbekend',
    turnaroundUnit: (minutes: number) => `u ${minutes}m`,
    turnaroundNoData: 'nog geen rapport goedgekeurd',
    waitingOver24: (n: number) =>
        n === 1 ? '1 wacht langer dan een dag' : `${n} wachten langer dan een dag`,
    orderCount: (n: number) => (n === 1 ? '1 keuring' : `${n} keuringen`),

    awaitingTitle: 'Rapporten die op je wachten',
    awaitingAll: 'Alle keuringen',
    awaitingAction: 'Afronden',
    awaitingEmpty: 'Er wacht geen rapport op je. Alles is verstuurd.',
    autoSendNote: 'Rapporten gaan vanzelf naar de klant zodra ze klaar zijn, zonder nalezen.',
    autoSendOffNote: 'Rapporten gaan pas naar de klant als jij ze goedkeurt.',
    autoSendOn: 'Automatisch versturen aan',
    autoSendOff: 'Automatisch versturen uit',
    waitingLabel: (address: string) => `Wachttijd van ${address}`,

    occupancyTitle: 'Bezetting deze week',
    occupancyNote: 'Hoeveel van de beschikbare uren bij jouw bedrijf al zijn ingepland.',
    occupancyLabel: (name: string) => `Bezetting ${name}`,
    occupancyUnknown: 'beschikbaarheid niet ingevuld',
    occupancyEmpty: 'Er is nog niemand ingepland deze week.',
    occupancyAction: 'Naar de planning',

    signalEyebrow: 'Let op',
    signalEmpty: 'Niets dat je aandacht nodig heeft. De week loopt zoals gepland.',

    loading: 'Dashboard laden',
    errorTitle: 'Het dashboard kon niet geladen worden',

    /* Aan de slag: de proef en de eerste stappen in één blok, met één volgende stap. */
    gettingStartedTitle: 'Aan de slag',
    gettingStartedProgress: (done: number, total: number) => `${done} van ${total} klaar`,
    gettingStartedHide: 'Lijstje verbergen',
    stepDone: 'Klaar',
    nextSteps: {
        first_inspection: {
            title: 'Begin met je eerste keuring',
            body: 'Vul de klant, het adres en de dienst in. Daarna doe je de opname op locatie, ook zonder internet, en stuur je het rapport met één klik naar je klant.',
            action: 'Nieuwe keuring',
        },
        company_details: {
            title: 'Vul je bedrijfsgegevens in',
            body: 'Je KvK-nummer en adres staan op elk rapport en elke factuur.',
            action: 'Bedrijfsgegevens invullen',
        },
        house_style: {
            title: 'Zet je logo op je rapporten',
            body: 'Je logo komt op het rapport, de klantpagina en de mail aan je klant.',
            action: 'Logo toevoegen',
        },
        choose_plan: {
            title: 'Kies een abonnement',
            body: 'Je proef loopt nog. Kies op tijd een abonnement, dan ga je zonder onderbreking door. Maandelijks opzegbaar.',
            action: 'Kies een abonnement',
        },
    },
    /** Als de gratis keuringen op zijn: dan zegt de kop dat, niet "kies een abonnement". */
    trialUsedTitle: 'Je gratis keuringen zijn gebruikt',
    trialUsedBody:
        'Kies een abonnement om nieuwe keuringen te maken. Alles wat je al deed, blijft staan.',
    /* De regel over de proef, onder de volgende stap. */
    trialRemaining: (n: number) =>
        n === 1
            ? 'Je hebt nog 1 gratis keuring, inclusief rapport.'
            : `Je hebt nog ${n} gratis keuringen, inclusief rapport.`,
    trialRunning: 'Je proef loopt. Elke keuring is inclusief rapport.',
    trialEndsOn: (date: string) => `De proef loopt tot ${date}.`,
    choosePlan: 'Kies een abonnement',

    /* De eerste stappen; weg zodra ze klaar of verborgen zijn. */
    onboardingSteps: {
        company_details: { label: 'Bedrijfsgegevens', action: 'Invullen' },
        house_style: { label: 'Logo en huisstijl', action: 'Instellen' },
        first_inspection: { label: 'Eerste keuring', action: 'Aanmaken' },
    },
    /** Zonder lijstje (verborgen of al klaar): hoe een keuring gaat, in drie stappen. */
    howItWorksLabel: 'Zo gaat een keuring',
    howItWorks: [
        'Maak de keuring aan met de klant, het adres en de dienst.',
        'Doe de opname op locatie, ook zonder internet.',
        'Keur het rapport goed. Je klant krijgt een link.',
    ],

    /* Een inspecteur bij een bedrijf zonder keuringen voor hem. */
    fieldEmptyTitle: 'Nog geen keuringen voor jou',
    fieldEmptyBody:
        'Zodra er een keuring op jouw naam staat, vind je hem in Mijn dag. Daar start je ook de opname, ook zonder internet.',
    fieldEmptyAction: 'Naar Mijn dag',
    fieldEmptyActionOffice: 'Naar Keuringen',
} as const;
